import { type FragmentOf, graphql, readFragment } from "@opensea/graphql"
import { useIsLessThanMd } from "@opensea/ui-kit"
import type { Nullable } from "@opensea/utils"

export const useCurrencyBannerMediaFragment = graphql(
  `
    fragment useCurrencyBannerMedia on Currency {
      metadata {
        hero {
          desktopHeroMedia {
            __typename
            ... on ImageMedia {
              imageUrl
            }
            ... on VideoMedia {
              videoUrl
            }
            ... on MuxVideoMedia {
              muxPlaybackId
            }
          }
          mobileHeroMedia {
            __typename
            ... on ImageMedia {
              imageUrl
            }
            ... on VideoMedia {
              videoUrl
            }
            ... on MuxVideoMedia {
              muxPlaybackId
            }
          }
        }
      }
    }
  `,
  [],
)

export const useCurrencyBannerMediaFallbackFragment = graphql(
  `
    fragment useCurrencyBannerMediaFallback on Currency {
      metadata {
        hero {
          desktopHeroMedia {
            ... on VideoMedia {
              thumbnailUrl
            }
            ... on MuxVideoMedia {
              thumbnailUrl
            }
          }
          mobileHeroMedia {
            ... on VideoMedia {
              thumbnailUrl
            }
            ... on MuxVideoMedia {
              thumbnailUrl
            }
          }
        }
      }
    }
  `,
  [],
)

export const useCurrencyBannerMedia = (
  currencyKey: Nullable<FragmentOf<typeof useCurrencyBannerMediaFragment>>,
): Nullable<string> => {
  const currency = readFragment(useCurrencyBannerMediaFragment, currencyKey)
  const isLessThanMd = useIsLessThanMd()
  const heroMedia = isLessThanMd
    ? currency?.metadata?.hero?.mobileHeroMedia
    : currency?.metadata?.hero?.desktopHeroMedia

  let heroMediaUrl = null

  switch (heroMedia?.__typename) {
    case "VideoMedia":
      heroMediaUrl = heroMedia.videoUrl
      break
    case "ImageMedia":
      heroMediaUrl = heroMedia.imageUrl
      break
    case "MuxVideoMedia":
      heroMediaUrl = heroMedia.muxPlaybackId
      break
    default:
      heroMediaUrl = null
  }

  return heroMediaUrl
}

export const useCurrencyBannerMediaFallback = (
  currencyKey: Nullable<
    FragmentOf<typeof useCurrencyBannerMediaFallbackFragment>
  >,
): Nullable<string> => {
  const currency = readFragment(
    useCurrencyBannerMediaFallbackFragment,
    currencyKey,
  )
  const isLessThanMd = useIsLessThanMd()
  const heroMedia = isLessThanMd
    ? currency?.metadata?.hero?.mobileHeroMedia
    : currency?.metadata?.hero?.desktopHeroMedia

  let fallbackMediaUrl = null

  switch (heroMedia?.__typename) {
    case "VideoMedia":
    case "MuxVideoMedia":
      fallbackMediaUrl = heroMedia.thumbnailUrl
      break
    default:
      fallbackMediaUrl = null
  }

  return fallbackMediaUrl
}
